import 'altcha';
import 'altcha/i18n/de';

// We don't use ALTCHA's interaction collector (it's meant for their hosted service).
globalThis.$altcha.defaults.set({ humanInteractionSignature: false });

/**
 * Sending logic shared by the contact page form and the speech bubble form (POST /api/v1/contact).
 *
 * The component provides: <form ref="contactForm"> containing
 * <altcha-widget ref="altcha" challenge="/api/v1/contact/challenge" @statechange="onAltchaStateChange">,
 * the honeypot input bound to `website`, `privacy` and `message` data fields (the message is cleared after
 * sending), and contactFields() returning its own fields (email, message, ...).
 */
export default {
    data() {
        return {
            website: '',
            altchaPayload: null,
            sendAfterVerification: false,
            sendingInProgress: null,
            mailSuccess: null,
            errorEmail: null,
            errorMessage: null,
            // Translation keys, so the texts follow a language switch.
            errorKeys: [],
        };
    },
    methods: {
        sendContactForm() {
            // The visitor's fields first, so a click on an incomplete form doesn't solve a challenge.
            // The widget's own checkbox is left out: it only becomes valid once the challenge is solved.
            const invalidField = Array.from(this.$refs.contactForm.elements)
                .find(field => !field.closest('altcha-widget') && !field.checkValidity());
            if (invalidField) {
                invalidField.reportValidity();
                return;
            }

            if (!this.privacy) {
                return;
            }

            if (!this.altchaPayload) {
                // Usually solved already (it starts when the form gets focus); otherwise solve it first and
                // come back here.
                this.sendAfterVerification = true;
                this.sendingInProgress = true;
                if (this.$refs.altcha.getState() !== 'verifying') {
                    this.$refs.altcha.verify();
                }
                return;
            }

            this.sendingInProgress = true;
            this.errorEmail = null;
            this.errorMessage = null;
            this.mailSuccess = null;
            this.errorKeys = [];

            this.postContactForm();
        },
        postContactForm() {
            window.axios
                .post('contact', Object.assign(this.contactFields(), {
                    privacy: this.privacy,
                    website: this.website,
                    altcha: this.altchaPayload,
                }))
                .then(() => {
                    this.mailSuccess = true;
                    this.sendingInProgress = null;
                    this.resetAltcha();
                    this.clearMessage();
                })
                .catch(error => {
                    const response = error.response;
                    const errors = response && response.status === 422 ? response.data.errors || {} : null;

                    if (!errors) {
                        this.errorKeys = ['kmso.contact.contact_form_error_general'];
                        this.resetAltcha();
                    } else {
                        const keys = [];

                        if (errors.email) {
                            this.errorEmail = true;
                            keys.push('kmso.contact.contact_form_error_email');
                        }

                        if (errors.message) {
                            this.errorMessage = true;
                            keys.push('kmso.contact.contact_form_error_message');
                        }

                        if (errors.altcha) {
                            keys.push('kmso.contact.contact_form_error_challenge');
                            this.resetAltcha();
                        }

                        if (errors.name || errors.phone || errors.subject || errors.privacy) {
                            keys.push('kmso.contact.contact_form_error_input');
                        }

                        this.errorKeys = keys.length ? keys : ['kmso.contact.contact_form_error_general'];
                    }

                    this.sendingInProgress = null;
                });
        },
        onAltchaStateChange(event) {
            const state = event.detail.state;
            this.altchaPayload = state === 'verified' ? event.detail.payload : null;

            if (this.sendAfterVerification && (state === 'verified' || state === 'error')) {
                this.sendAfterVerification = false;
                this.sendingInProgress = null;

                if (state === 'verified') {
                    this.sendContactForm();
                } else {
                    this.errorKeys = ['kmso.contact.contact_form_error_general'];
                }
            }
        },
        // A solved challenge counts only once, so every request needs a fresh one.
        resetAltcha() {
            this.altchaPayload = null;
            this.$refs.altcha.reset();
        },
        // After sending, so another click on the button doesn't send the same text again.
        clearMessage() {
            this.message = null;

            // Also hide the green check that $validateInput showed next to the message field.
            const textarea = this.$refs.contactForm.querySelector('textarea');
            const okIcon = textarea && document.getElementById(textarea.id + 'Success');
            if (okIcon) {
                okIcon.classList.add('display-none');
            }
        },
    },
};
